import React, { useCallback, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { usePlaceInfo } from "../../hooks/usePlaceInfo";
import { placeStatusToText } from "../../lib/place-status-to-text";
import { renderSortedOptions } from "../../lib/render-sorted-options";
import { createPlaceDataSubmission } from "../../services/placeService";
import { PlaceDataSubmission } from "../../types/Place";
import { PlaceStatus } from "../../types/PlaceStatus";
import { VibeType } from "../../types/VibeType";
import { debugLogError } from "../../utils/debug-log-error";
import VibeSelector from "../VibeSelector/VibeSelector";
import "./AddPlaceInfoModal.scss";

interface AddPlaceInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  googlePlaceId: string;
}

const AddPlaceInfoModal: React.FC<AddPlaceInfoModalProps> = ({
  isOpen,
  onClose,
  googlePlaceId,
}) => {
  const { place, loading: placeLoading } = usePlaceInfo(googlePlaceId);

  const { user } = useAppAuthState();
  const [selectedStatus, setSelectedStatus] = useState<PlaceStatus | undefined>(
    undefined
  );
  const [selectedVibes, setSelectedVibes] = useState<VibeType[]>(
    place?.vibes ?? []
  );
  const [numberOfTables, setNumberOfTables] = useState<number | undefined>(
    place?.numberOfTables ?? undefined
  );

  const handleStatusChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      if (event.target.value === "") {
        setSelectedStatus(undefined);
      } else {
        setSelectedStatus(event.target.value as PlaceStatus);
      }
    },
    []
  );

  const handleVibeChange = useCallback(
    (vibes: VibeType[]) => {
      setSelectedVibes(vibes);
    },
    [setSelectedVibes]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      try {
        let hasContributions = false;
        const submissionData: Partial<PlaceDataSubmission> = {
          placeId: googlePlaceId,
          creatorId: user?.uid,
        };
        if (selectedStatus) {
          submissionData.status = selectedStatus;
          hasContributions = true;
        }
        if (selectedVibes.length > 0) {
          submissionData.vibes = selectedVibes;
          hasContributions = true;
        }
        if (numberOfTables !== undefined) {
          submissionData.numberOfTables = numberOfTables;
          hasContributions = true;
        }

        if (!hasContributions) {
          toast.error("Please make at least one contribution.");
          return;
        }

        await createPlaceDataSubmission(submissionData);
        toast.success(
          "Thank you for your submission! We'll review it shortly."
        );
        onClose();
      } catch (error) {
        debugLogError("AddPlaceInfoModal", error);
      }
    },
    [
      googlePlaceId,
      onClose,
      selectedStatus,
      selectedVibes,
      numberOfTables,
      user,
    ]
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="add-place-info-modal"
      overlayClassName="add-place-info-modal-overlay"
      contentLabel="Add Place Info"
    >
      <div className="modal-header">
        <h2>Add Place Info</h2>
        <button className="close-button" onClick={onClose}>
          <i className="material-icons">close</i>
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="place-status-select">Status:</label>
          <select
            id="place-status-select"
            value={selectedStatus ?? ""}
            onChange={handleStatusChange}
          >
            <option value="">Select a status</option>
            {renderSortedOptions(
              Object.values(PlaceStatus),
              placeStatusToText,
              [
                PlaceStatus.PENDING,
                PlaceStatus.REJECTED,
                PlaceStatus.UNDER_REVIEW,
                PlaceStatus.UNKNOWN,
              ]
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="place-table-count">Number of tables:</label>
          <input
            type="number"
            className="form-control"
            id="place-table-count"
            name="place-table-count"
            min="0"
            value={numberOfTables ?? ""}
            onChange={(event) => setNumberOfTables(event.target.valueAsNumber)}
          />
          <p className="form-text text-muted">
            If you don't know the exact number of tables, an estimate is fine.
          </p>
        </div>
        <div className="form-group">
          <label htmlFor="place-vibes">Vibes:</label>
          <VibeSelector
            value={selectedVibes}
            onChange={handleVibeChange}
            disabled={placeLoading}
          />
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddPlaceInfoModal;
